/**
 * Centralized Token Service
 * Prevents race conditions and manages token refresh
 * CRITICAL FIX: Uses localStorage instead of getSession() to prevent hangs
 */

import { supabase } from '@/integrations/supabase/client';
import { getAccessToken } from '@/shared/utils/supabaseStorage';

class TokenService {
  private refreshPromise: Promise<string> | null = null;
  private tokenRefreshThreshold = 60; // Refresh if token expires within 60 seconds

  /**
   * Get current auth token from localStorage
   * Returns null if no valid session exists
   * CRITICAL FIX: Uses localStorage instead of getSession() to prevent hangs
   */
  async getToken(): Promise<string | null> {
    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        console.warn('[TokenService] No access token found');
        return null;
      }
      return accessToken;
    } catch (error) {
      console.error('[TokenService] Error getting token:', error);
      return null;
    }
  }

  /**
   * Refresh the authentication token
   * Prevents multiple simultaneous refresh attempts
   * @throws Error if refresh fails
   */
  async refreshToken(): Promise<string> {
    // Prevent multiple simultaneous refreshes (race condition protection)
    if (this.refreshPromise) {
      console.log('[TokenService] Refresh already in progress, waiting...');
      return this.refreshPromise;
    }

    console.log('[TokenService] Starting token refresh...');

    this.refreshPromise = (async () => {
      try {
        const { data, error } = await supabase.auth.refreshSession();

        if (error || !data.session) {
          console.error('[TokenService] Refresh failed:', error);
          throw new Error('Session expired. Please log in again.');
        }

        console.log('[TokenService] Token refreshed successfully');
        return data.session.access_token;
      } finally {
        // Clear the promise after completion (success or failure)
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Check if token is expired or will expire soon
   */
  async isTokenExpiringSoon(): Promise<boolean> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session || !session.expires_at) {
        return true;
      }

      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = session.expires_at - now;

      return timeUntilExpiry < this.tokenRefreshThreshold;
    } catch (error) {
      console.error('[TokenService] Error checking token expiry:', error);
      return true;
    }
  }

  /**
   * Get user ID from current session
   */
  async getUserId(): Promise<string | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.user?.id ?? null;
    } catch (error) {
      console.error('[TokenService] Error getting user ID:', error);
      return null;
    }
  }

  /**
   * Get current session
   */
  async getSession() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      return { session, error };
    } catch (error) {
      console.error('[TokenService] Error getting session:', error);
      return { session: null, error };
    }
  }
}

export const tokenService = new TokenService();
